import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  LinearProgress,
  Stack,
  Card,
  CardContent,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Check as CheckIcon,
  AccountCircle as ProfileIcon,
  DirectionsCar as LicenseIcon,
  School as EducationIcon,
  Work as WorkIcon,
  CardMembership as CertificationIcon,
} from '@mui/icons-material';
import DriverSnapshot from './sections/DriverSnapshot';
import DrivingLicenses from './sections/DrivingLicenses';
import Education from './sections/Education';
import EmploymentExperience from './sections/EmploymentExperience';
import Certifications from './sections/Certifications';

const steps = [
  {
    label: 'Driver Snapshot',
    icon: ProfileIcon,
    component: DriverSnapshot,
  },
  {
    label: 'Driving Licenses',
    icon: LicenseIcon,
    component: DrivingLicenses,
  },
  {
    label: 'Education',
    icon: EducationIcon,
    component: Education,
  },
  {
    label: 'Employment',
    icon: WorkIcon,
    component: EmploymentExperience,
  },
  {
    label: 'Certifications',
    icon: CertificationIcon,
    component: Certifications,
  },
];

interface ProfileData {
  snapshot: {
    radiusOfExperience?: string;
    canadaExperience?: any;
    usaExperience?: any;
    internationalExperience?: any;
    accidentHistory?: any;
    ticketHistory?: any;
    citizenship?: any;
    drugAndAlcoholTesting?: any;
    passport?: any;
  };
  licenses: any[];
  education: any[];
  employment: any[];
  certifications: any[];
}

const DriverProfileBuilder: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [profileData, setProfileData] = useState<ProfileData>({
    snapshot: {},
    licenses: [],
    education: [],
    employment: [],
    certifications: [],
  });
  const [completedSteps] = useState<Set<number>>(new Set());

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(prevStep => prevStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(prevStep => prevStep - 1);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    setActiveStep(stepIndex);
  };

  const handleSaveAndExit = () => {
    // TODO: Save profile data
    // Navigate back to drivers list
    navigate('/drivers');
  };

  const updateProfileData = (section: keyof ProfileData, data: any) => {
    setProfileData(prev => ({
      ...prev,
      [section]: data,
    }));
  };

  const progressPercentage = Math.round((completedSteps.size / steps.length) * 100);
  const ActiveComponent = steps[activeStep].component;

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', py: 2 }}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" fontWeight="600">
              Driver Profile Builder
            </Typography>
            <Button variant="outlined" onClick={handleSaveAndExit} sx={{ borderRadius: 2 }}>
              Save & Exit
            </Button>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {/* Left Sidebar - Progress and Navigation */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                backgroundColor: '#e0e0e0',
                borderRadius: 2,
                position: 'sticky',
                top: 20,
              }}
            >
              {/* Progress */}
              <Box sx={{ mb: 4 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6" fontWeight="600">
                    {progressPercentage}% complete
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {completedSteps.size} of {steps.length} sections
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={progressPercentage}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#c0c0c0',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      backgroundColor: '#2e7d32',
                    },
                  }}
                />
              </Box>

              {/* Welcome Message */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" fontWeight="700" gutterBottom>
                  Welcome aboard to Driver Verified
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Let&apos;s set up your Driver Profile
                </Typography>
              </Box>

              {/* Navigation Steps */}
              <Stack spacing={2}>
                {steps.map((step, index) => {
                  const Icon = step.icon;
                  const isActive = activeStep === index;
                  const isCompleted = completedSteps.has(index);

                  return (
                    <Card
                      key={index}
                      elevation={0}
                      sx={{
                        border: '1px solid',
                        borderColor: isActive ? '#2e7d32' : '#e0e0e0',
                        backgroundColor: isActive ? 'white' : 'transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: '#2e7d32',
                          backgroundColor: 'white',
                        },
                      }}
                      onClick={() => handleStepClick(index)}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1,
                                backgroundColor: isActive ? '#2e7d32' : '#f5f5f5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Icon sx={{ color: isActive ? 'white' : 'text.secondary' }} />
                            </Box>
                            <Typography variant="body1" fontWeight={isActive ? 600 : 400}>
                              {step.label}
                            </Typography>
                          </Stack>
                          {isCompleted && <CheckIcon sx={{ color: '#2e7d32' }} />}
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </Paper>
          </Grid>

          {/* Right Content Area */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
              {/* Section Header */}
              <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Section {activeStep + 1} of {steps.length}
                </Typography>
                <Typography variant="h4" fontWeight="700">
                  {steps[activeStep].label}
                </Typography>
                <Typography variant="body1" color="text.secondary" mt={1}>
                  Setup your profile to unlock the DriverVerified Report Center and Resume Export
                </Typography>
              </Box>

              {/* Section Content */}
              <Box sx={{ p: 4 }}>
                <ActiveComponent
                  data={
                    activeStep === 0
                      ? profileData.snapshot
                      : activeStep === 1
                      ? profileData.licenses
                      : activeStep === 2
                      ? profileData.education
                      : activeStep === 3
                      ? profileData.employment
                      : profileData.certifications
                  }
                  onChange={(data: any) => {
                    const sectionKey =
                      activeStep === 0
                        ? 'snapshot'
                        : activeStep === 1
                        ? 'licenses'
                        : activeStep === 2
                        ? 'education'
                        : activeStep === 3
                        ? 'employment'
                        : 'certifications';
                    updateProfileData(sectionKey as keyof ProfileData, data);
                  }}
                />
              </Box>

              {/* Navigation Buttons */}
              <Box sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
                <Stack direction="row" justifyContent="space-between">
                  <Button
                    variant="outlined"
                    startIcon={<ChevronLeftIcon />}
                    onClick={handleBack}
                    disabled={activeStep === 0}
                    sx={{ borderRadius: 2 }}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="contained"
                    endIcon={<ChevronRightIcon />}
                    onClick={handleNext}
                    disabled={activeStep === steps.length - 1}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: '#212121',
                      '&:hover': {
                        backgroundColor: '#424242',
                      },
                    }}
                  >
                    Next
                  </Button>
                </Stack>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default DriverProfileBuilder;
